import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { WAITLIST } from '@/constants/testIds';
import { useI18n } from '@/i18n/I18nProvider';
import { BRAND_SIZES } from '@/i18n/messages';
import CertificationsPicker from './CertificationsPicker';

const initialState = {
  brand_name: '',
  contact_name: '',
  email: '',
  website: '',
  brand_size: '',
  certifications: [], // stores canonical EN values
  notes: '',
  company_url: '', // honeypot
};

export default function WaitlistForm() {
  const { m, locale } = useI18n();
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const toggleCertById = (id) => {
    setForm((f) => {
      const has = f.certifications.includes(id);
      return {
        ...f,
        certifications: has
          ? f.certifications.filter((c) => c !== id)
          : [...f.certifications, id],
      };
    });
  };

  const validate = () => {
    const e = {};
    if (!form.brand_name.trim()) e.brand_name = m.waitlist.errors.brandName;
    if (!form.contact_name.trim()) e.contact_name = m.waitlist.errors.contactName;
    if (!form.email.trim()) e.email = m.waitlist.errors.emailRequired;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = m.waitlist.errors.emailInvalid;
    if (form.website && !/^([a-z0-9-]+\.)+[a-z]{2,}|^https?:\/\//i.test(form.website))
      e.website = m.waitlist.errors.websiteInvalid;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post('/waitlist', form);
      setSuccess(true);
      toast.success(m.waitlist.toastSuccess);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((d) => d?.msg || 'Invalid field').join(', ')
        : detail || m.waitlist.errors.generic;
      toast.error(message);
      setErrors((er) => ({ ...er, _form: message }));
    } finally {
      setSubmitting(false);
    }
  };

  const showCount = form.certifications.length;

  return (
    <section id="waitlist" data-testid={WAITLIST.section} className="relative section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] items-start">
          <div className="max-w-md">
            <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
              {m.waitlist.eyebrow}
            </div>
            <h2 className="font-display mt-2 text-3xl sm:text-4xl tracking-[-0.01em]">
              {m.waitlist.headingA}
              <span className="text-muted-foreground">{m.waitlist.headingB}</span>
              {m.waitlist.headingC}
            </h2>
            <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
              {m.waitlist.lead}
            </p>
            <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              {m.waitlist.privacy}
            </div>
          </div>

          <div className="relative">
            <AnimatePresence mode="wait">
              {success ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  data-testid={WAITLIST.success}
                  className="rounded-2xl border border-accent/40 bg-card p-8 sm:p-10 glow-ring"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full border border-accent/40 bg-accent/10 flex items-center justify-center">
                      <CheckCircle2 className="h-5 w-5 text-accent" />
                    </div>
                    <div className="font-display text-2xl">{m.waitlist.successTitle}</div>
                  </div>
                  <p className="mt-4 text-muted-foreground leading-relaxed">
                    {m.waitlist.successBody}
                  </p>
                  <div className="mt-6 grid sm:grid-cols-3 gap-3">
                    <Stat title={m.waitlist.successStats.stage} value={m.waitlist.successStats.stageValue} />
                    <Stat title={m.waitlist.successStats.slots} value={m.waitlist.successStats.slotsValue} />
                    <Stat title={m.waitlist.successStats.next} value={m.waitlist.successStats.nextValue} />
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  onSubmit={onSubmit}
                  noValidate
                  data-testid={WAITLIST.form}
                  className="relative rounded-2xl border border-border bg-card p-6 sm:p-8 glow-ring"
                >
                  <div className="absolute inset-0 pointer-events-none rounded-2xl"
                    aria-hidden
                    style={{
                      background:
                        'radial-gradient(420px circle at 100% 0%, hsla(173,72%,40%,0.10), transparent 50%)',
                    }} />
                  <div className="relative grid gap-5 md:grid-cols-2">
                    <Field id="brand_name" label={m.waitlist.fields.brandName} required error={errors.brand_name}>
                      <Input
                        id="brand_name"
                        data-testid={WAITLIST.brandName}
                        value={form.brand_name}
                        onChange={update('brand_name')}
                        placeholder={m.waitlist.placeholders.brandName}
                        className="h-11 bg-secondary/50 border-border focus-visible:ring-ring"
                      />
                    </Field>

                    <Field id="contact_name" label={m.waitlist.fields.contactName} required error={errors.contact_name}>
                      <Input
                        id="contact_name"
                        data-testid={WAITLIST.contactName}
                        value={form.contact_name}
                        onChange={update('contact_name')}
                        placeholder={m.waitlist.placeholders.contactName}
                        className="h-11 bg-secondary/50 border-border focus-visible:ring-ring"
                      />
                    </Field>

                    <Field id="email" label={m.waitlist.fields.email} required error={errors.email}>
                      <Input
                        id="email"
                        type="email"
                        data-testid={WAITLIST.email}
                        value={form.email}
                        onChange={update('email')}
                        placeholder={m.waitlist.placeholders.email}
                        className="h-11 bg-secondary/50 border-border focus-visible:ring-ring"
                      />
                    </Field>

                    <Field id="website" label={m.waitlist.fields.website} error={errors.website}>
                      <Input
                        id="website"
                        data-testid={WAITLIST.website}
                        value={form.website}
                        onChange={update('website')}
                        placeholder={m.waitlist.placeholders.website}
                        className="h-11 bg-secondary/50 border-border focus-visible:ring-ring"
                      />
                    </Field>

                    <Field id="brand_size" label={m.waitlist.fields.brandSize}>
                      <Select
                        value={form.brand_size}
                        onValueChange={(v) => setForm((f) => ({ ...f, brand_size: v }))}
                      >
                        <SelectTrigger
                          id="brand_size"
                          data-testid={WAITLIST.brandSize}
                          className="h-11 bg-secondary/50 border-border focus:ring-ring"
                        >
                          <SelectValue placeholder={m.waitlist.placeholders.brandSize} />
                        </SelectTrigger>
                        <SelectContent>
                          {BRAND_SIZES.map((s) => (
                            <SelectItem key={s} value={s}>
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>

                    <div className="md:col-span-2">
                      <Field
                        id="certifications"
                        label={`${m.waitlist.fields.certifications}${showCount ? ` · ${showCount} ${m.waitlist.selected || 'selected'}` : ''}`}
                      >
                        <CertificationsPicker
                          selected={form.certifications}
                          locale={locale}
                          onToggle={toggleCertById}
                          labels={{
                            industry: m.waitlist.fields.industry,
                            industryPlaceholder: m.waitlist.placeholders.industry,
                            picker: m.waitlist.fields.certifications,
                            pickerPlaceholder: m.waitlist.placeholders.certifications,
                            selectedOne: m.waitlist.certsSelectedOne,
                            selectedMany: m.waitlist.certsSelectedMany,
                            emptyForIndustry: m.waitlist.certsEmpty,
                            pickIndustryFirst: m.waitlist.pickIndustryFirst,
                            remove: m.waitlist.removeCert,
                          }}
                        />
                      </Field>
                    </div>

                    <div className="md:col-span-2">
                      <Field id="notes" label={m.waitlist.fields.notes}>
                        <Textarea
                          id="notes"
                          data-testid={WAITLIST.notes}
                          value={form.notes}
                          onChange={update('notes')}
                          placeholder={m.waitlist.placeholders.notes}
                          className="min-h-[96px] bg-secondary/50 border-border focus-visible:ring-ring"
                        />
                      </Field>
                    </div>

                    {/* Honeypot */}
                    <div className="hidden" aria-hidden>
                      <label htmlFor="company_url">Company URL</label>
                      <input
                        id="company_url"
                        data-testid={WAITLIST.honeypot}
                        autoComplete="off"
                        tabIndex={-1}
                        value={form.company_url}
                        onChange={update('company_url')}
                      />
                    </div>

                    <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
                      <div className="text-xs text-muted-foreground max-w-sm">
                        {m.waitlist.consent}
                      </div>
                      <Button
                        type="submit"
                        disabled={submitting}
                        data-testid={WAITLIST.submit}
                        className="h-11 px-5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 min-w-[180px]"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            {m.waitlist.submitting}
                          </>
                        ) : (
                          <>
                            {m.waitlist.submit}
                            <ArrowRight className="ml-1.5 h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </div>

                    {errors._form && (
                      <div
                        data-testid={WAITLIST.error}
                        className="md:col-span-2 text-sm text-destructive"
                      >
                        {errors._form}
                      </div>
                    )}
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ id, label, required, error, children }) {
  return (
    <div>
      <Label htmlFor={id} className="text-xs uppercase tracking-wider text-muted-foreground">
        {label} {required && <span className="text-primary">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {error && <div className="mt-1 text-xs text-destructive">{error}</div>}
    </div>
  );
}

function Stat({ title, value }) {
  return (
    <div className="rounded-xl border border-border bg-secondary/40 px-3 py-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{title}</div>
      <div className="mt-1 font-display text-base leading-none">{value}</div>
    </div>
  );
}
